import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { Recipe } from '../types/Recipe';
import { getRecipes, addRecipe, updateRecipe, deleteRecipe } from '../utils/localStorage';

interface RecipeContextType {
  recipes: Recipe[];
  loading: boolean;
  addNewRecipe: (recipe: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateExistingRecipe: (id: string, recipe: Partial<Recipe>) => void;
  removeRecipe: (id: string) => void;
  getRecipeById: (id: string) => Recipe | undefined;
  filterRecipes: (filters: RecipeFilters) => Recipe[];
}

export interface RecipeFilters {
  search?: string;
  category?: string;
  difficulty?: string;
  maxTime?: number;
}

export const RecipeContext = createContext<RecipeContextType>({
  recipes: [],
  loading: true,
  addNewRecipe: () => {},
  updateExistingRecipe: () => {},
  removeRecipe: () => {},
  getRecipeById: () => undefined,
  filterRecipes: () => [],
});

interface RecipeProviderProps {
  children: ReactNode;
}

export const RecipeProvider: React.FC<RecipeProviderProps> = ({ children }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Charger les recettes depuis le localStorage au montage du composant
    const loadRecipes = () => {
      const storedRecipes = getRecipes();
      setRecipes(storedRecipes);
      setLoading(false);
    };

    loadRecipes();
  }, []);

  const addNewRecipe = (recipe: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newRecipe = addRecipe(recipe);
    setRecipes(prev => [...prev, newRecipe]);
  };

  const updateExistingRecipe = (id: string, recipeData: Partial<Recipe>) => {
    const updatedRecipe = updateRecipe(id, recipeData);
    if (updatedRecipe) {
      setRecipes(prev => prev.map(recipe => recipe.id === id ? updatedRecipe : recipe));
    }
  };

  const removeRecipe = (id: string) => {
    const success = deleteRecipe(id);
    if (success) {
      setRecipes(prev => prev.filter(recipe => recipe.id !== id));
    }
  };

  const getRecipeById = (id: string) => {
    return recipes.find(recipe => recipe.id === id);
  };

  const filterRecipes = (filters: RecipeFilters) => {
    return recipes.filter(recipe => {
      // Filtre par recherche textuelle
      if (filters.search && !recipe.title.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      
      // Filtre par catégorie
      if (filters.category && recipe.category !== filters.category) {
        return false;
      }
      
      // Filtre par difficulté
      if (filters.difficulty && recipe.difficulty !== filters.difficulty) {
        return false;
      }
      
      // Filtre par temps total
      if (filters.maxTime && (recipe.prepTime + recipe.cookTime) > filters.maxTime) {
        return false;
      }
      
      return true;
    });
  };

  return (
    <RecipeContext.Provider value={{
      recipes,
      loading,
      addNewRecipe,
      updateExistingRecipe,
      removeRecipe,
      getRecipeById,
      filterRecipes
    }}>
      {children}
    </RecipeContext.Provider>
  );
};
